import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Close, Edit } from '@pay-com/icons'
import { Button, IconButton } from '@pay-com/components'
import {
  IndividualType,
  CountryAlpha2,
  useCreateIndividualWithRelationMutation,
  IndividualContactType,
  useGetMerchantApplicationQuery,
  useRemoveIndividualMutation,
  useUpdateIndividualMutation,
  useUpdateIndividualRelationMutation,
  IndividualWithRelations
} from 'generated/graphql'
import { useTranslation } from 'react-i18next'
import { BeneficialOwner } from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs/BeneficialOwner/beneficial-owner-schema'
import { ErrorMessage } from 'components/ErrorMessage'
import { IndividualWithRelationsFormValues } from 'pages/Onboarding'
import { BeneficialOwnerModal } from './BeneficialOwnerModal/BeneficialOwnerModal'

type ModalState =
  | { open: false }
  | { open: true; mode: 'add' }
  | {
      open: true
      mode: 'edit'
      owner: BeneficialOwner
      index: number
      id: string
    }

const mapFormToIndividualInput = (owner: BeneficialOwner) => ({
  individual: {
    type: IndividualType.Individual,
    firstName: owner.individual.firstName,
    middleName: owner.individual.middleName || null,
    lastName: owner.individual.lastName,
    dateOfBirth: owner.individual.dateOfBirth?.toString() || null,
    nationalities: owner.individual.nationalities as CountryAlpha2[],
    ssn: owner.individual.ssn || null,
    idNumber: owner.individual.idNumber || null,
    identificationDocumentType:
      owner.individual.identificationDocumentType || null,
    email: owner.individual.email,
    phoneNumber: owner.individual.phoneNumber,
    residentialAddress: {
      addressLine: owner.individual.residentialAddress.addressLine,
      addressLine2: owner.individual.residentialAddress.addressLine2 || null,
      city: owner.individual.residentialAddress.city,
      zip: owner.individual.residentialAddress.zip,
      state: owner.individual.residentialAddress.state,
      countryAlpha2: owner.individual.residentialAddress.countryAlpha2
    }
  },
  individualRelation: {
    title: owner.relation.title,
    isShareholder: true,
    isUbo: true,
    percentageOfShares: owner.relation.percentageOfShares,
    isContact: owner.relation.isContact || false,
    contactTypes: owner.relation.isContact
      ? [IndividualContactType.Chargeback]
      : [],
    signsTaxDocuments: owner.relation.signsTaxDocuments || false
  }
})

const mapToOwner = (item: IndividualWithRelations) => {
  const { individual, parentRelations } = item
  const relation = parentRelations?.find(r => r.isUbo)

  return {
    individual: {
      firstName: individual.firstName ?? '',
      middleName: individual.middleName,
      lastName: individual.lastName ?? '',
      dateOfBirth: individual.dateOfBirth ? Number(individual.dateOfBirth) : 0,
      nationalities: (individual.nationalities ?? []) as string[],
      ssn: individual.ssn ?? '',
      idNumber: individual.idNumber,
      identificationDocumentType: individual.identificationDocumentType,
      email: individual.email ?? '',
      phoneNumber: individual.phoneNumber ?? '',
      residentialAddress: {
        countryAlpha2: individual.residentialAddress?.countryAlpha2 || null,
        addressLine: individual.residentialAddress?.addressLine ?? '',
        addressLine2: individual.residentialAddress?.addressLine2,
        city: individual.residentialAddress?.city ?? '',
        state: individual.residentialAddress?.state ?? '',
        zip: individual.residentialAddress?.zip ?? ''
      }
    },
    relation: {
      title: relation?.title ?? '',
      percentageOfShares: String(relation?.percentageOfShares ?? ''),
      signsTaxDocuments: relation?.signsTaxDocuments,
      isContact: relation?.isContact
    }
  }
}

export const BeneficialOwnerField = () => {
  const { data: merchantData } = useGetMerchantApplicationQuery()
  const { t } = useTranslation()
  const merchantId = merchantData?.merchantApplication?.merchantId ?? ''

  const {
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useFormContext<IndividualWithRelationsFormValues>()
  const [modal, setModal] = useState<ModalState>({ open: false })

  const refetchQueries = ['getNestedIndividuals']

  const [createIndividual, { loading: creating }] =
    useCreateIndividualWithRelationMutation({ refetchQueries })
  const [updateIndividual, { loading: updating }] = useUpdateIndividualMutation(
    { refetchQueries }
  )
  const [updateIndividualRelation, { loading: updatingRelation }] =
    useUpdateIndividualRelationMutation({ refetchQueries })
  const [removeIndividual] = useRemoveIndividualMutation({ refetchQueries })

  const isSaving = creating || updating || updatingRelation

  const allIndividuals: IndividualWithRelations[] = watch('individuals') ?? []

  const ubos = allIndividuals
    .map((ind, index) => ({ ...ind, originalIndex: index }))
    .filter(ind => ind.parentRelations?.some(rel => rel.isUbo))

  const editingUboId = modal.open && modal.mode === 'edit' ? modal.id : null

  const signerTaken = allIndividuals.some(
    ind =>
      ind.individual.id !== editingUboId &&
      ind.parentRelations?.some(rel => rel.signsTaxDocuments)
  )
  const contactTaken = allIndividuals.some(
    ind =>
      ind.individual.id !== editingUboId &&
      ind.parentRelations?.some(
        rel =>
          rel.isContact &&
          rel.contactTypes?.includes(IndividualContactType.Chargeback)
      )
  )

  const onConfirm = async (owner: BeneficialOwner) => {
    const current = getValues('individuals') as IndividualWithRelations[]
    const mapped = mapFormToIndividualInput(owner)

    if (modal.open && modal.mode === 'edit') {
      await updateIndividual({
        variables: { input: { id: modal.id, ...mapped.individual } }
      })
      await updateIndividualRelation({
        variables: {
          input: {
            individualId: modal.id,
            relatedEntityId: merchantId,
            ...mapped.individualRelation
          }
        }
      })

      const updated = current.map((ind, i) =>
        i === modal.index
          ? {
              ...ind,
              individual: { ...ind.individual, ...mapped.individual },
              parentRelations: ind.parentRelations?.map(rel =>
                rel.isUbo
                  ? {
                      ...rel,
                      ...mapped.individualRelation,
                      individualId: modal.id
                    }
                  : rel
              )
            }
          : ind
      )
      setValue('individuals', updated, { shouldDirty: true })
    } else {
      const result = await createIndividual({
        variables: { input: mapped }
      })

      const newId = result.data?.createIndividualWithRelation?.id ?? ''

      setValue(
        'individuals',
        [
          ...current,
          {
            individual: { ...mapped.individual, id: newId },
            parentRelations: [
              { individualId: newId, ...mapped.individualRelation }
            ]
          }
        ],
        { shouldDirty: true }
      )
    }

    setModal({ open: false })
  }

  const onRemove = async (id: string, index: number) => {
    await removeIndividual({ variables: { input: { id } } })

    const current = getValues('individuals') as IndividualWithRelations[]
    setValue(
      'individuals',
      current.filter((_, i) => i !== index),
      { shouldDirty: true }
    )
  }

  const individualsError = (errors.individuals?.root?.message ??
    errors.individuals?.message) as string | undefined

  return (
    <div className="flex flex-col">
      <BeneficialOwnerModal
        isOpen={modal.open}
        defaultValues={
          modal.open && modal.mode === 'edit' ? modal.owner : undefined
        }
        isEdit={modal.open && modal.mode === 'edit'}
        isSaving={isSaving}
        signerTaken={signerTaken}
        contactTaken={contactTaken}
        onConfirm={onConfirm}
        onDismiss={() => setModal({ open: false })}
      />
      <div className="flex flex-col gap-2">
        {ubos.map(ubo => {
          const relation = ubo.parentRelations?.find(r => r.isUbo)

          return (
            <div
              key={ubo.individual.id}
              className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 p-3 last:mb-10 md:p-6"
            >
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {ubo.individual.firstName} {ubo.individual.lastName}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Number(relation?.percentageOfShares)}%
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {ubo.individual.email} | {relation?.title}
                </span>
              </div>
              <div className="flex gap-2">
                <IconButton
                  variant="secondary"
                  type="button"
                  title="edit row"
                  testId="button-edit-owner"
                  onClick={() =>
                    setModal({
                      open: true,
                      mode: 'edit',
                      owner: mapToOwner(ubo),
                      index: ubo.originalIndex,
                      id: ubo.individual.id
                    })
                  }
                >
                  <Edit color="primary" width={20} />
                </IconButton>
                <IconButton
                  type="button"
                  variant="secondary"
                  title="delete row"
                  testId="button-delete-owner"
                  onClick={() => onRemove(ubo.individual.id, ubo.originalIndex)}
                >
                  <Close color="primary" width={20} />
                </IconButton>
              </div>
            </div>
          )
        })}
      </div>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setModal({ open: true, mode: 'add' })}
        className="w-56"
      >
        + {t('beneficialOwner.modal.addButton')}
      </Button>
      <ErrorMessage errorMessage={individualsError} />
    </div>
  )
}
